import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { HttpHeaderUtil } from '../utils/http-header-util';
import { Attachment } from './attachment';

/**
 * Service stellt Methoden zum Upload, Download und zum Überprüfen des Status einer Datei bereit.
 * Ist mit unserer "Attachment-Strategie" verwoben. Für weitere Details siehe {@link FileUploadComponent}.
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */
@Injectable()
export class FileUploadService {

    /**
     * Dieser Header wird an Uploads angehangen um die Requests klassifizieren zu können.
     */
    public static readonly UPLOAD_ID_HEADER = 'X-Upload-ID';

    constructor(private http: HttpClient, private httpHeaderUtil: HttpHeaderUtil) {
    }

    /**
     * Führt für das übergebene File einen POST an den übergebenen Endpunkt aus.
     *
     * @param item das File das hochgeladen werden soll
     * @param endpoint der Endpunkt
     * @param id ID zum identifizieren eines Uploads
     * @returns {Observable<R>} HTTP-Observable
     */
    public uploadFile(item: File, endpoint: string, id: string = item.name) {
        const formData: FormData = new FormData();
        formData.append('file', item, encodeURIComponent(item.name));
        let headers = this.httpHeaderUtil.appendForRequest(FileUploadService.UPLOAD_ID_HEADER, id);
        headers = this.httpHeaderUtil.deleteForRequest('Content-Type', headers);
        const options = { headers: headers };
        return this.http.post(endpoint, formData, options);
    }

    /**
     * Prüft den Status für das Attachment mit der übergebenen UUID
     *
     * @param uuid die UUID des Attachments
     * @param endpoint der Endpunkt
     * @returns Observable<Attachment> HTTP-Observable
     */
    public checkState(uuid: string, endpoint: string): Observable<Attachment> {
        return this.http.get<Attachment>(endpoint + uuid + '/metadata').pipe(timeout(20000));
    }

    /**
     * Lädt das Attachment mit der übergebenen UUID herunter.
     *
     * @param uuid die UUID des Attachments
     * @param endpoint der Endpunkt
     * @returns Observable<any>
     */
    public download(uuid: string, endpoint: string): Observable<any> {
        const options = { responseType: 'blob' as 'blob', observe: 'response' as 'response' };
        return this.http.get(endpoint + uuid, options)
            .pipe(
                map((res) => {
                    saveAs(res.body, FileUploadService.getFileName(res, 'attachment'));
                    return res;
                }));
    }

    /**
     * Generiert eine ID, die als ID an {@link #upload} übergeben werden kann.
     *
     * @param prefix optional der Dateiname z.B..
     */
    public static generateId(prefix?: string): string {
        let result = prefix ? prefix + '-' : '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < 8; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    /**
     * Ermittelt den Dateinamen aus dem Content-Disposition-Header. Falls kein Dateiname ermittelt werden kann
     * wird als default der übergebene Dateiname verwendet.
     *
     * @param httpResponse die HTTP-Response an der der Content-Disposition-Header gesetzt ist
     * @param fallback der default-Dateiname falls keiner aus dem Header ermittelt werden kann
     */
    private static getFileName(httpResponse: HttpResponse<Blob>, fallback: string): string {
        const header = httpResponse.headers.get('Content-Disposition');
        const result = header && header.split(';')[1].trim().split('=')[1].replace(/"/g, '');
        return result || fallback;
    }
}
